import { useContext, useMemo, useState, type ChangeEvent } from 'react';

import { useDebounce } from '@mint-lib/hooks';
import { useTranslation } from '@mint-lib/i18n';
import { ArrowLeft } from '@mint-lib/icons';
import { Slots } from '@mint-lib/slots';
import { Button, Divider } from '@mint-lib/tw-components';
import { ExpandableSidebar, SearchBar } from '@mint-lib/tw-components/patterns';

import { useSidebarTreeApi } from '../../../api/queries/useSidebarTreeApi.js';
import {
  EMPTY_SIDEBAR_TREE,
  SHOW_PLANNING_SIDEBAR,
} from '../../../consts/sidebar.js';
import { filterTreeItems, normalizeText } from '../../../utils/sidebar.js';
import SidebarList from './SidebarList.jsx';
import SidebarSkeleton from './SidebarSkeleton.jsx';

const SideBar = () => {
  const { t } = useTranslation('@myn/planning_v2');

  const [searchValue, setSearchValue] = useState('');

  const debouncedSearchValue = useDebounce(searchValue, 500);

  const manager = useContext(Slots);

  const handleHideSidebar = () => {
    manager.removeSlot('sidebar', SHOW_PLANNING_SIDEBAR);
  };

  const { data: sidebarTree = EMPTY_SIDEBAR_TREE, isPending } =
    useSidebarTreeApi();

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const filteredSidebarTree = useMemo(() => {
    if (!debouncedSearchValue) {
      return sidebarTree;
    }

    const isRootMatch = normalizeText(sidebarTree.name).includes(
      normalizeText(debouncedSearchValue),
    );

    if (isRootMatch) {
      return sidebarTree;
    }

    const filteredItems = filterTreeItems(
      debouncedSearchValue,
      sidebarTree.subItems,
    );

    return {
      ...sidebarTree,
      subItems: filteredItems,
    };
  }, [debouncedSearchValue, sidebarTree]);

  return (
    <ExpandableSidebar>
      <div className="relative h-full w-full">
        <div className="absolute top-0 bg-ui-primary pt-1">
          <Button
            onClick={handleHideSidebar}
            variant="text"
            aria-label="Back to main sidebar"
          >
            <ArrowLeft />
            {t('Back')}
          </Button>
          <Divider className="mt-2 mb-2" />
          <SearchBar
            onChange={handleSearch}
            size="small"
            placeholder={t('Search')}
            data-dd-action-name="Planning sidebar: search"
          />
        </div>
        <div className="absolute top-22 bottom-4 w-full overflow-x-hidden overflow-y-auto">
          {isPending ? (
            <SidebarSkeleton />
          ) : (
            <SidebarList
              // on each search change we should re-render the sidebar list to reset the expanded state and show the filtered items
              key={debouncedSearchValue}
              sidebarTree={filteredSidebarTree}
            />
          )}
        </div>
      </div>
    </ExpandableSidebar>
  );
};

export default SideBar;
